import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import styles from './AdminProjectAdmin.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../reducers/store';
import useAdminProjectParams from '../../../hooks/useAdminProjectParams';
import AdminProjectAdminsSlice from '../../../slices/AdminProjectAdminsSlice';
import { useTranslate } from 'react-translate.ts';
import AdminProjectLayout from '../AdminProjectLayout/AdminProjectLayout';
import { Form, Formik, useFormikContext } from 'formik';
import useDebounce from '../../../../helpers/useDebounce';
import AdminFormSelect from '../../input/AdminFormSelect';
import { ButtonVariants, Skeleton } from '@tcc-online/common-components';
import RequestService from '@tcc-online/request-service';
import EditProjectSubmit from '../../../pages/CreateProjectPage/components/EditProjectSubmit/EditProjectSubmit';
import { Link } from 'react-router-dom';
import BaseCard from '../../../../components/cards/BaseCard/BaseCard';
import ProjectAdminType, {
    ProjectAdminNotificationType,
    ProjectAdminTypeType,
} from '../../../types/ProjectAdminType';
import useDate from '../../../../hooks/useDate';
import AdminButton from '../../buttons/AdminButton/AdminButton';
import AdminProjectAdminsNotificationValues from '../../../constants/AdminProjectAdminsNotificationValues';
import AdminProjectAdminsTypeValues from '../../../constants/AdminProjectAdminsTypeValues';
import AdminSelect from '../../input/AdminSelect';
import BasePageLayout from '../../../../common/components/BasePageLayout/BasePageLayout';

const FormValues = {
    userId: 'userId',
    type: 'type',
    notificationSetting: 'notificationSetting',
};

type FormValuesType = {
    userId: number | null;
    type: ProjectAdminTypeType;
    notificationSetting: ProjectAdminNotificationType;
};

export default function AdminProjectAdmin() {
    const dispatch = useDispatch<AppDispatch>();
    const { productId, projectId } = useAdminProjectParams();
    const { data: admins, loading } = useSelector(
        AdminProjectAdminsSlice.state.all,
    );
    const translate = useTranslate();

    useEffect(() => {
        dispatch(
            AdminProjectAdminsSlice.actions.getAdmins({
                productType: productId,
                projectId,
            }),
        );
    }, [productId, projectId]);

    return (
        <BasePageLayout>
            <AdminProjectLayout
                heading={translate('Administratori')}
                classNames={{ heading: styles.heading }}
            >
                <BaseCard className={styles.card}>
                    <Formik<FormValuesType>
                        initialValues={{
                            userId: null,
                            type: 1,
                            notificationSetting: 0,
                        }}
                        enableReinitialize
                        onSubmit={submit(productId, projectId)}
                    >
                        <AddForm isLoading={loading} />
                    </Formik>
                </BaseCard>
                <BaseCard className={styles.list}>
                    <div className={styles.table}>
                        <div className={styles.row}>
                            <div className={styles.head}>
                                {translate('Email') /* TODO: translate */}
                            </div>
                            <div className={styles.head}>
                                {translate('Jmeno') /* TODO: translate */}
                            </div>
                            <div className={styles.head}>
                                {translate('Prijmeni') /* TODO: translate */}
                            </div>
                            <div className={styles.head}>
                                {translate('Role') /* TODO: translate */}
                            </div>
                            <div className={styles.head}>
                                {translate('Notifikace') /* TODO: translate */}
                            </div>
                            <div className={styles.head}>
                                {translate('Prirazen') /* TODO: translate */}
                            </div>
                        </div>
                        {admins?.map((admin: ProjectAdminType) =>
                            !loading ? (
                                <ProjectAdmin
                                    key={admin.id}
                                    admin={admin}
                                    loading={!!loading}
                                />
                            ) : (
                                <Skeleton className={styles.skeleton} />
                            ),
                        )}
                    </div>
                </BaseCard>
                <Link to="../">
                    <EditProjectSubmit>
                        {translate('Zpet na detail')}
                    </EditProjectSubmit>
                </Link>
            </AdminProjectLayout>
        </BasePageLayout>
    );

    function submit(productId: number, projectId: number) {
        return (values: FormValuesType) => {
            const { userId, type, notificationSetting } = values;
            if (userId === null) {
                return;
            }
            dispatch(
                AdminProjectAdminsSlice.actions.createAdmin({
                    productType: productId,
                    projectId,
                    userId,
                    type,
                    notificationSetting,
                }),
            );
        };
    }
}

const TypeOptions = Object.values(AdminProjectAdminsTypeValues).filter(
    (option) => option.value !== 3,
);
const NotificationOptions = Object.values(AdminProjectAdminsNotificationValues);

function AddForm(props: { isLoading?: boolean }) {
    const { isLoading } = props;
    const translate = useTranslate();
    const [emailOptions, setEmailOptions] = useState<ProjectAdminType[]>([]);
    const emailOptionsRef: MutableRefObject<ProjectAdminType[]> = useRef([]);
    const formik = useFormikContext<FormValuesType>();
    const debounce = useDebounce<any>();

    return (
        <Form className={styles.form}>
            <AdminFormSelect
                options={emailOptions}
                name={FormValues.userId}
                placeholder={translate('Email')}
                searchable
                searchFunction={async (options, search: string) => {
                    return await debounce(searchEmails, options, search);
                }}
                getOptionValue={(option: ProjectAdminType) => option.id}
                getOptionLabel={(option: any) =>
                    option.user?.email || option.email
                }
                onChange={async (value) => {
                    if (value === null) {
                        await formik.setValues({
                            ...formik.values,
                            [FormValues.userId]: null,
                        });
                        return;
                    }

                    const index = emailOptionsRef.current.findIndex(
                        (option) => option.id === value,
                    );

                    if (index >= 0) {
                        const option = emailOptionsRef.current[index];
                        setEmailOptions([option]);

                        await formik.setValues({
                            ...formik.values,
                            [FormValues.userId]: value,
                        });
                    }
                }}
                disabled={isLoading}
                async
            />
            <AdminFormSelect
                name={FormValues.type}
                placeholder={translate('Role')}
                disabled={isLoading}
                options={TypeOptions}
                getOptionLabel={(option) => translate(option.label)}
            />
            <AdminFormSelect
                name={FormValues.notificationSetting}
                placeholder={translate('Notifikace')}
                disabled={isLoading}
                options={NotificationOptions}
                getOptionLabel={(option) => translate(option.label)}
            />
            <AdminButton
                variant={ButtonVariants.positive}
                type="submit"
                className={styles.button}
                disabled={isLoading}
                onClick={submit}
            >
                {translate('Pridat')}
            </AdminButton>
        </Form>
    );

    async function submit() {
        await formik.submitForm();
        formik.resetForm();
    }

    async function searchEmails(options, search: string) {
        if (search.trim().length >= 3) {
            const response = await RequestService.get(`/users?role=manager`, {
                search,
            });
            emailOptionsRef.current = response.data as ProjectAdminType[];
            return response.data;
        }

        return options;
    }
}

function ProjectAdmin(props: { admin: ProjectAdminType; loading: boolean }) {
    const {
        loading,
        admin: { id, user, creationDate, type, notificationSetting },
    } = props;
    const translate = useTranslate();
    const { productId, projectId } = useAdminProjectParams();
    const dispatch = useDispatch<AppDispatch>();

    const date = useDate(creationDate);
    return (
        <div className={styles.row}>
            <div>{user?.email}</div>
            <div>{user?.firstName}</div>
            <div>{user?.lastName}</div>
            <AdminSelect
                name={FormValues.type}
                placeholder={translate('Role')}
                disabled={loading}
                options={TypeOptions}
                getOptionLabel={(option) => translate(option.label)}
                value={AdminProjectAdminsTypeValues[type]}
                onChange={updateType({
                    projectId,
                    productId,
                    adminId: id,
                })}
            />
            <AdminSelect
                name={FormValues.notificationSetting}
                placeholder={translate('Notifikace')}
                disabled={loading}
                options={NotificationOptions}
                getOptionLabel={(option) => translate(option.label)}
                value={
                    AdminProjectAdminsNotificationValues[notificationSetting]
                }
                onChange={updateNotification({
                    projectId,
                    productId,
                    adminId: id,
                })}
            />
            <div>{date}</div>
        </div>
    );

    function updateType(params: {
        adminId: number;
        productId: number;
        projectId: number;
    }) {
        return (type: ProjectAdminTypeType) => {
            update({ ...params, type });
        };
    }

    function updateNotification(params: {
        adminId: number;
        productId: number;
        projectId: number;
    }) {
        return (notificationSetting: ProjectAdminNotificationType) => {
            update({ ...params, notificationSetting });
        };
    }

    function update(params: {
        adminId: number;
        productId: number;
        projectId: number;
        type?: ProjectAdminTypeType;
        notificationSetting?: ProjectAdminNotificationType;
    }) {
        const { adminId, productId, projectId, notificationSetting, type } =
            params;
        dispatch(
            AdminProjectAdminsSlice.actions.updateAdmin({
                adminId,
                productType: productId,
                projectId,
                type,
                notificationSetting,
            }),
        );
    }
}
